import { Button, Field, makeStyles, Select, Text } from "@fluentui/react-components";
import { ChevronRightFilled, DismissRegular } from "@fluentui/react-icons";
import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { AddStation } from "./AddStation";

export interface StationSelectorViewProps {
  regionOptions: { key: string; text: string }[];
  cafeOptions: { key: string; text: string }[];
  stationOptions: { key: string; name: string }[];
  onRegionSelect: (option?: { key: string; text: string }) => void;
  onCafeSelect: (option?: { key: string; text: string }) => void;
  onStationClick: (station: { key: string; name: string }) => void;
  onStationDeleteClick: () => void;
  selectedRegionId?: string;
  selectedCafeId?: string;
  selectedStation?: string;
}

const useClasses = makeStyles({
  stationContainer: {
    display: "flex",
    alignItems: "center",
    padding: "0px",
    borderRadius: "4px",
    border: "1px solid transparent",
    width: "100%",
    maxWidth: "100%",
  },
  stationContainerSelected: {
    border: "2px solid var(--link-color) !important",
    borderRadius: "0px",
    width: "90%",
  },
  stationButton: {
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    textAlign: "left",
    padding: "8px 12px",
    flex: 1,
    minWidth: 0,
  },
  stationButtonSelected: {
    border: "none !important",
    outline: "none !important",
    boxShadow: "none !important",
    ":focus": {
      border: "none !important",
      outline: "none !important",
      boxShadow: "none !important",
    },
    ":focus-visible": {
      border: "none !important",
      outline: "none !important",
      boxShadow: "none !important",
    },
    ":active": {
      border: "none !important",
      outline: "none !important",
      boxShadow: "none !important",
    },
    ":hover": {
      border: "none !important",
      outline: "none !important",
      boxShadow: "none !important",
    },
  },
  stationRow: { display: "flex", alignItems: "center", width: "100%", minWidth: 0, padding: 0 },
  caretIcon: { display: "flex", alignItems: "center", fontSize: "16px" },
  caretHidden: { visibility: "hidden" },
  stationTextWrapper: {
    display: "flex",
    alignItems: "center",
    border: "none",
    gap: "6px",
    flex: 1,
    minWidth: 0,
    padding: 0,
    width: "100%",
  },
  deleteButton: {
    flexShrink: 0,
    color: "var(--link-color) !important",
    backgroundColor: "transparent !important",
  },
  cafeDropdownFix: {
    overflow: "visible !important",
    whiteSpace: "normal !important",
    textOverflow: "unset !important",
  },
});

const StationSelectorView = ({
  regionOptions,
  cafeOptions,
  stationOptions,
  onRegionSelect,
  onCafeSelect,
  onStationClick,
  onStationDeleteClick,
  selectedRegionId,
  selectedCafeId,
  selectedStation,
}: StationSelectorViewProps) => {
  const classes = useClasses();

  const [selectedRegion, setSelectedRegion] = useState<{ key: string; text: string } | undefined>(
    regionOptions.find((r) => r.key === selectedRegionId),
  );

  const [selectedCafe, setSelectedCafe] = useState<{ key: string; text: string } | undefined>(
    cafeOptions.find((c) => c.key === selectedCafeId),
  );

  const [announceMessage, setAnnounceMessage] = useState("");
  const stationRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    if (!announceMessage) return;
    const timer = setTimeout(() => setAnnounceMessage(""), 2000);
    return () => clearTimeout(timer);
  }, [announceMessage]);

  useEffect(() => {
    if (selectedStation) {
      stationRefs.current[selectedStation]?.focus();
    }
  }, [selectedStation]);

  useEffect(() => {
    if (!selectedCafeId) {
      setSelectedCafe(undefined);
    } else {
      const cafe = cafeOptions.find((c) => c.key === selectedCafeId);
      setSelectedCafe(cafe);
    }
  }, [selectedCafeId, cafeOptions]);

  const handleRegionChange = (regionKey: string) => {
    const option = regionOptions.find((o) => o.key === regionKey);
    setSelectedRegion(option);
    onRegionSelect(option);

    setSelectedCafe(undefined);
    onCafeSelect(undefined);
  };

  const handleCafeSelect = (option?: { key: string; text: string }) => {
    setSelectedCafe(option);
    onCafeSelect(option);
  };

  const handleStationClick = (station: { key: string; name: string }) => {
    onStationClick(station);
    setAnnounceMessage(`Choose a station ${station.name} button selected`);
  };

  return (
    <div className="container">
      <div className="inner">
        {/* Region Select */}
        <Field label="Choose a Region">
          <Select
            value={selectedRegion?.key ?? ""}
            onChange={(e) => handleRegionChange(e.target.value)}
            data-testid="regionDropdown"
          >
            <option value="" disabled>
              {"Choose a region... "}
            </option>
            {regionOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.text}
              </option>
            ))}
          </Select>
        </Field>

        {/* Cafe Dropdown */}
        <Field label="Choose a Cafe">
          <Select
            value={selectedCafe?.key ?? ""}
            onChange={(e) => {
              const option = cafeOptions.find((c) => c.key === e.target.value);
              handleCafeSelect(option);
            }}
            disabled={!cafeOptions.length}
            data-testid="cafeDropdown"
          >
            <option value="" disabled>
              Choose a cafe...
            </option>
            {cafeOptions.map((opt) => (
              <option key={opt.key} value={opt.key}>
                {opt.text}
              </option>
            ))}
          </Select>
        </Field>

        {/* Station List */}
        <Text weight="semibold">Choose a Station</Text>

        {stationOptions.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {stationOptions.map((station) => {
              const isSelected = station.key === selectedStation;

              return (
                <div
                  key={station.key}
                  className={classNames(classes.stationContainer, isSelected && classes.stationContainerSelected)}
                >
                  <button
                    type="button"
                    aria-label={`${station.name}${isSelected ? ", selected" : ""}`}
                    ref={(el) => (stationRefs.current[station.key] = el)}
                    className={classNames(classes.stationButton, isSelected && classes.stationButtonSelected)}
                    onClick={() => handleStationClick(station)}
                  >
                    <div className={classes.stationTextWrapper}>
                      <span className={classNames(classes.caretIcon, !isSelected && classes.caretHidden)}>
                        <ChevronRightFilled />
                      </span>
                      <span
                        title={station.name}
                        style={{
                          color: !isSelected ? "var(--link-color)" : "black",
                          fontWeight: isSelected ? 700 : 400,
                        }}
                      >
                        {station.name}
                      </span>
                    </div>
                  </button>

                  {isSelected && (
                    <Button
                      appearance="subtle"
                      icon={<DismissRegular />}
                      aria-label={`Remove ${station.name}`}
                      className={classes.deleteButton}
                      onClick={onStationDeleteClick}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedCafe && <AddStation />}

      {/* Screen reader announcement */}
      <span
        aria-live="polite"
        style={{
          position: "absolute",
          width: "1px",
          height: "1px",
          margin: "-1px",
          border: "0",
          padding: "0",
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
        }}
      >
        {announceMessage}
      </span>
    </div>
  );
};

export default StationSelectorView;
